import { useState } from "react";
import { useTranslation } from "react-i18next";
import { fmtDate } from "@/lib/locale";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend,
} from "recharts";
import type { LucideIcon } from "lucide-react";
import {
  DollarSign, Zap, Activity, TrendingUp, Brain, AlertTriangle,
  BarChart2, Users, Server,
} from "lucide-react";

// ─── Helper ──────────────────────────────────────────────────────────────────

function centsToDisplay(cents: number): string {
  if (cents === 0) return "$0.00";
  const dollars = cents / 100;
  if (dollars < 0.01) return `<$0.01`;
  return `$${dollars.toFixed(2)}`;
}

// Fetched from /api/super-admin/ai-features so it stays in sync with the server enum.
function useFeatureLabels(): Record<string, string> {
  const { data } = useQuery<{ value: string; label: string }[]>({
    queryKey: ["/api/super-admin/ai-features"],
    staleTime: Infinity,
  });
  if (!data) return {};
  return Object.fromEntries(data.map((f) => [f.value, f.label]));
}

function featureLabelFrom(labels: Record<string, string>, feature: string): string {
  return labels[feature] ?? feature;
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, color, testId }: {
  icon: LucideIcon; label: string; value: string; sub?: string; color?: string; testId: string;
}) {
  return (
    <Card>
      <CardContent className="p-5 flex items-start gap-4">
        <div className={`p-2 rounded-lg ${color ?? "bg-primary/10"}`}>
          <Icon className={`w-5 h-5 ${color ? "text-white" : "text-primary"}`} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold text-foreground" data-testid={`stat-${testId}`}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Cost Section ─────────────────────────────────────────────────────────────

function CostSection() {
  const { t } = useTranslation(["pages"]);
  const featureLabels = useFeatureLabels();
  const { data: summary, isLoading: loadingSummary } = useQuery<any>({
    queryKey: ["/api/super-admin/ai-costs/summary"],
  });
  const { data: daily, isLoading: loadingDaily } = useQuery<any[]>({
    queryKey: ["/api/super-admin/ai-costs/daily"],
  });
  const { data: byFeature, isLoading: loadingFeature } = useQuery<any[]>({
    queryKey: ["/api/super-admin/ai-costs/by-feature"],
  });
  const { data: byProvider, isLoading: loadingProvider } = useQuery<any[]>({
    queryKey: ["/api/super-admin/ai-costs/by-provider"],
  });
  const { data: byDealer, isLoading: loadingDealer } = useQuery<any[]>({
    queryKey: ["/api/super-admin/ai-costs/by-dealer"],
  });
  const { data: deanByDealer, isLoading: loadingDean } = useQuery<any[]>({
    queryKey: ["/api/super-admin/ai-costs/dean-chat-by-dealer"],
  });

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {loadingSummary ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}><CardContent className="p-5"><Skeleton className="h-16 w-full" /></CardContent></Card>
          ))
        ) : (
          <>
            <StatCard icon={DollarSign} label={t("pages:aiIntelligence.cost.allTimeCost")} testId="all-time-cost" value={centsToDisplay(summary?.allTimeCents ?? 0)} />
            <StatCard icon={DollarSign} label={t("pages:aiIntelligence.cost.thisMonth")} testId="this-month" value={centsToDisplay(summary?.thisMonthCents ?? 0)} />
            <StatCard icon={DollarSign} label={t("pages:aiIntelligence.cost.today")} testId="today" value={centsToDisplay(summary?.todayCents ?? 0)} />
            <StatCard icon={Activity} label={t("pages:aiIntelligence.cost.totalApiCalls")} testId="total-api-calls" value={(summary?.totalCalls ?? 0).toLocaleString()} sub={t("pages:aiIntelligence.cost.tokensSub", { in: ((summary?.totalInputTokens ?? 0) / 1_000_000).toFixed(2), out: ((summary?.totalOutputTokens ?? 0) / 1_000_000).toFixed(2) })} />
          </>
        )}
      </div>

      {/* Daily spend chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BarChart2 className="w-4 h-4" /> {t("pages:aiIntelligence.cost.dailySpend")}</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingDaily ? <Skeleton className="h-48 w-full" /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={(daily ?? []).map((d) => ({ date: d.date?.slice(5), cost: (d.costCents / 100).toFixed(4), calls: d.callCount }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v: number | string) => [`$${parseFloat(String(v)).toFixed(4)}`, t("pages:aiIntelligence.cost.costLabel")]} />
                <Bar dataKey="cost" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Feature + Provider breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Zap className="w-4 h-4" /> {t("pages:aiIntelligence.cost.costByFeature")}</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingFeature ? <Skeleton className="h-40 w-full" /> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("pages:aiIntelligence.cost.feature")}</TableHead>
                    <TableHead className="text-right">{t("pages:aiIntelligence.cost.costLabel")}</TableHead>
                    <TableHead className="text-right">{t("pages:aiIntelligence.cost.calls")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(byFeature ?? []).map((r) => (
                    <TableRow key={r.feature} data-testid={`row-feature-${r.feature}`}>
                      <TableCell>{featureLabelFrom(featureLabels, r.feature)}</TableCell>
                      <TableCell className="text-right font-mono">{centsToDisplay(r.costCents)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{r.callCount.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  {(byFeature ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">{t("pages:aiIntelligence.noDataYet")}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Server className="w-4 h-4" /> {t("pages:aiIntelligence.cost.providerBreakdown")}</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingProvider ? <Skeleton className="h-40 w-full" /> : (
              <div className="space-y-4">
                {(byProvider ?? []).map((r) => {
                  const pct = summary?.allTimeCents > 0 ? Math.round((r.costCents / summary.allTimeCents) * 100) : 0;
                  return (
                    <div key={r.provider} data-testid={`row-provider-${r.provider}`}>
                      <div className="flex justify-between mb-1">
                        <span className="capitalize font-medium">{r.provider}</span>
                        <span className="text-muted-foreground">{centsToDisplay(r.costCents)} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full">
                        <div className="h-2 bg-primary rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{t("pages:aiIntelligence.cost.callsCount", { count: r.callCount.toLocaleString() })}</p>
                    </div>
                  );
                })}
                {(byProvider ?? []).length === 0 && <p className="text-center text-muted-foreground py-6">{t("pages:aiIntelligence.noDataYet")}</p>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* DEAN Chat per-dealer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Brain className="w-4 h-4" /> {t("pages:aiIntelligence.cost.deanChatPerDealer")}</CardTitle>
          <CardDescription>{t("pages:aiIntelligence.cost.deanChatPerDealerDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingDean ? <Skeleton className="h-40 w-full" /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages:aiIntelligence.cost.dealer")}</TableHead>
                  <TableHead className="text-right">{t("pages:aiIntelligence.cost.deanCost")}</TableHead>
                  <TableHead className="text-right">{t("pages:aiIntelligence.cost.calls")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(deanByDealer ?? []).slice(0, 20).map((r) => (
                  <TableRow key={r.dealerId} data-testid={`row-dean-dealer-${r.dealerId}`}>
                    <TableCell>
                      <div className="font-medium">{r.companyName ?? r.username}</div>
                      <div className="text-xs text-muted-foreground">{r.username}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{centsToDisplay(r.costCents)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{r.callCount.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                {(deanByDealer ?? []).length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">{t("pages:aiIntelligence.cost.noDeanUsage")}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Overall per-dealer cost */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="w-4 h-4" /> {t("pages:aiIntelligence.cost.overallPerDealer")}</CardTitle>
          <CardDescription>{t("pages:aiIntelligence.cost.overallPerDealerDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingDealer ? <Skeleton className="h-48 w-full" /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages:aiIntelligence.cost.dealer")}</TableHead>
                  <TableHead className="text-right">{t("pages:aiIntelligence.cost.totalCost")}</TableHead>
                  <TableHead className="text-right">{t("pages:aiIntelligence.cost.totalCalls")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(byDealer ?? []).slice(0, 30).map((r) => (
                  <TableRow key={r.dealerId} data-testid={`row-dealer-${r.dealerId}`}>
                    <TableCell>
                      <div className="font-medium">{r.companyName ?? r.username}</div>
                      <div className="text-xs text-muted-foreground">{r.username}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{centsToDisplay(r.costCents)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{r.callCount.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                {(byDealer ?? []).length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">{t("pages:aiIntelligence.cost.noDealerUsage")}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Personas Section ─────────────────────────────────────────────────────────

function PersonasSection() {
  const { t } = useTranslation(["pages"]);
  const featureLabels = useFeatureLabels();
  const { data: deanConf, isLoading: loadingDean } = useQuery<any[]>({
    queryKey: ["/api/super-admin/ai-intelligence/dean-confidence"],
  });
  const { data: deanByDealer, isLoading: loadingDeanDealer } = useQuery<any[]>({
    queryKey: ["/api/super-admin/ai-intelligence/dean-confidence-by-dealer"],
  });
  const { data: deansSonConf, isLoading: loadingDeansSon } = useQuery<any[]>({
    queryKey: ["/api/super-admin/ai-intelligence/deans-son-confidence"],
  });
  const { data: lowConf, isLoading: loadingLow } = useQuery<any[]>({
    queryKey: ["/api/super-admin/ai-intelligence/low-confidence-responses"],
  });

  // Merge DEAN and Dean's Son data for comparison chart
  const comparisonData = (() => {
    const deanMap = new Map((deanConf ?? []).map((d) => [d.date?.slice(0, 10), d.avgConfidence]));
    const deansSonMap = new Map((deansSonConf ?? []).map((d) => [d.date?.slice(0, 10), d.avgConfidence]));
    const allDates = [...new Set([...deanMap.keys(), ...deansSonMap.keys()])].sort();
    return allDates.map((date) => ({
      date: date?.slice(5),
      dean: deanMap.get(date) ? (deanMap.get(date)! * 100).toFixed(1) : null,
      deansSon: deansSonMap.get(date) ? (deansSonMap.get(date)! * 100).toFixed(1) : null,
    }));
  })();

  return (
    <div className="space-y-6">
      {/* DEAN confidence over time */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Brain className="w-4 h-4" /> {t("pages:aiIntelligence.personas.deanConfidence")}</CardTitle>
          <CardDescription>{t("pages:aiIntelligence.personas.deanConfidenceDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingDean ? <Skeleton className="h-48 w-full" /> : (deanConf ?? []).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Brain className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">{t("pages:aiIntelligence.personas.noConfidenceData")}</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={(deanConf ?? []).map((d) => ({ date: d.date?.slice(5), confidence: (d.avgConfidence * 100).toFixed(1) }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v: number | string) => [`${v}%`, t("pages:aiIntelligence.personas.avgConfidence")]} />
                <Line type="monotone" dataKey="confidence" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* DEAN confidence per dealer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="w-4 h-4" /> {t("pages:aiIntelligence.personas.deanConfidencePerDealer")}</CardTitle>
          <CardDescription>{t("pages:aiIntelligence.personas.deanConfidencePerDealerDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingDeanDealer ? <Skeleton className="h-40 w-full" /> : (deanByDealer ?? []).length === 0 ? (
            <p className="text-center text-muted-foreground py-6">{t("pages:aiIntelligence.personas.noPerDealerData")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages:aiIntelligence.personas.dealer")}</TableHead>
                  <TableHead className="text-right">{t("pages:aiIntelligence.personas.avgConfidenceCol")}</TableHead>
                  <TableHead className="text-right">{t("pages:aiIntelligence.personas.min")}</TableHead>
                  <TableHead className="text-right">{t("pages:aiIntelligence.personas.responses")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(deanByDealer ?? []).slice(0, 20).map((r) => (
                  <TableRow key={r.dealerId} data-testid={`row-dean-conf-dealer-${r.dealerId}`}>
                    <TableCell>
                      <div className="font-medium">{r.companyName ?? r.username}</div>
                      <div className="text-xs text-muted-foreground">{r.username}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={r.avgConfidence < 0.6 ? "destructive" : r.avgConfidence < 0.8 ? "secondary" : "default"}>
                        {(r.avgConfidence * 100).toFixed(0)}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">{(r.minConfidence * 100).toFixed(0)}%</TableCell>
                    <TableCell className="text-right text-muted-foreground">{r.responseCount.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dean's Son learning trend + comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><TrendingUp className="w-4 h-4" /> {t("pages:aiIntelligence.personas.deansSonProgress")}</CardTitle>
          <CardDescription>{t("pages:aiIntelligence.personas.deansSonProgressDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingDeansSon ? <Skeleton className="h-48 w-full" /> : comparisonData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <TrendingUp className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">{t("pages:aiIntelligence.personas.deansSonEmpty")}</p>
              <p className="text-xs mt-1 text-muted-foreground/70">{t("pages:aiIntelligence.personas.deansSonEmptySub")}</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v: number | string, name: string) => [`${v}%`, name === "dean" ? t("pages:aiIntelligence.personas.deanClaude") : t("pages:aiIntelligence.personas.deansSonProvisional")]} />
                <Legend formatter={(val) => val === "dean" ? t("pages:aiIntelligence.personas.deanClaude") : t("pages:aiIntelligence.personas.deansSonProvisionalDerived")} />
                <Line type="monotone" dataKey="dean" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="deansSon" stroke="hsl(220, 70%, 60%)" strokeWidth={2} strokeDasharray="5 5" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Low confidence flagged responses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-4 h-4" /> {t("pages:aiIntelligence.personas.lowConfidence")}
          </CardTitle>
          <CardDescription>{t("pages:aiIntelligence.personas.lowConfidenceDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingLow ? <Skeleton className="h-40 w-full" /> : (lowConf ?? []).length === 0 ? (
            <p className="text-center text-muted-foreground py-6">{t("pages:aiIntelligence.personas.noLowConfidence")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages:aiIntelligence.personas.dealer")}</TableHead>
                  <TableHead>{t("pages:aiIntelligence.personas.feature")}</TableHead>
                  <TableHead className="text-right">{t("pages:aiIntelligence.personas.confidence")}</TableHead>
                  <TableHead className="text-right">{t("pages:aiIntelligence.personas.when")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(lowConf ?? []).slice(0, 20).map((r) => (
                  <TableRow key={r.id} data-testid={`row-low-conf-${r.id}`}>
                    <TableCell>
                      <div className="font-medium">{r.companyName ?? r.username ?? t("pages:aiIntelligence.personas.unknown")}</div>
                      <div className="text-xs text-muted-foreground">{r.model}</div>
                    </TableCell>
                    <TableCell>{featureLabelFrom(featureLabels, r.feature)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="destructive">{(r.confidenceScore * 100).toFixed(0)}%</Badge>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground text-xs">
                      {r.createdAt ? fmtDate(r.createdAt) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AiIntelligencePage() {
  const { t } = useTranslation(["pages"]);
  const [tab, setTab] = useState<"cost" | "personas">("cost");

  function handleTabChange(v: string) {
    if (v === "cost" || v === "personas") setTab(v);
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Brain className="w-6 h-6 text-primary" />
            {t("pages:aiIntelligence.title")}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t("pages:aiIntelligence.subtitle")}
          </p>
        </div>
        <Badge variant="secondary" className="text-xs">{t("pages:aiIntelligence.superAdmin")}</Badge>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="cost" data-testid="tab-cost" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> {t("pages:aiIntelligence.tabCost")}
          </TabsTrigger>
          <TabsTrigger value="personas" data-testid="tab-personas" className="flex items-center gap-2">
            <Brain className="w-4 h-4" /> {t("pages:aiIntelligence.tabPersonas")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cost" className="mt-6">
          <CostSection />
        </TabsContent>

        <TabsContent value="personas" className="mt-6">
          <PersonasSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
